import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import QuestionnaireActions from '../../participant/actions/QuestionnaireActions';
import UserService from '../services/UserService';
import IBaseState from '../../types/IBaseState';
import UserInfo from '../types/UserInfo';
import UserActions from '../../actions/UserActions';

const NAME = 'User';

export type UserState = {
    activeQuestionnaires: IBaseState<any[]>;
    info: IBaseState<UserInfo | undefined>;
};

const initialState: UserState = {
    activeQuestionnaires: {
        data: [],
        loading: false,
        requestId: undefined,
    },
    info: {
        data: undefined,
        loading: false,
        requestId: undefined,
    },
};

const getActiveQuestionnaires = createAsyncThunk(
    `${NAME}/getActiveQuestionnaires`,
    UserService.getActiveQuestionnaires,
);

const getInfo = createAsyncThunk(`${NAME}/getInfo`, UserService.getInfo);

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getActiveQuestionnaires.pending,
            (state: UserState, action) => {
                if (state.activeQuestionnaires.loading === false) {
                    state.activeQuestionnaires.loading = true;
                    state.activeQuestionnaires.requestId =
                        action.meta.requestId;
                }
            },
        );
        builder.addCase(
            getActiveQuestionnaires.fulfilled,
            (state: UserState, action) => {
                if (
                    state.activeQuestionnaires.loading === true &&
                    action.meta.requestId ===
                        state.activeQuestionnaires.requestId
                ) {
                    state.activeQuestionnaires.data =
                        action.payload?.data || [];
                }
            },
        );
        builder.addCase(
            QuestionnaireActions.closeQuestionnaire,
            (state: UserState, action) => {
                if (state.activeQuestionnaires.data !== null) {
                    const index = state.activeQuestionnaires.data.findIndex(
                        // @ts-ignore TODO: fix
                        (item) => item.id === action.payload.id,
                    );

                    if (index >= 0) {
                        state.activeQuestionnaires.data.splice(index, 1);
                    }
                }
            },
        );

        builder.addCase(getInfo.pending, (state: UserState, action) => {
            if (state.info.loading === false) {
                state.info.loading = true;
                state.info.requestId = action.meta.requestId;
            }
        });
        builder.addCase(getInfo.fulfilled, (state: UserState, action) => {
            if (
                state.info.loading === true &&
                action.meta.requestId === state.info.requestId
            ) {
                state.info.data = action.payload?.data;
            }
        });
        builder.addCase(
            UserActions.changePassword.fulfilled,
            (state: UserState) => {
                if (state.info.data) {
                    state.info.data.forceChangePassword = false;
                }
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(
                getActiveQuestionnaires.fulfilled,
                getActiveQuestionnaires.rejected,
            ),
            (state: UserState, action) => {
                if (
                    state.activeQuestionnaires.loading === true &&
                    action.meta.requestId ===
                        state.activeQuestionnaires.requestId
                ) {
                    state.activeQuestionnaires.loading = false;
                    state.activeQuestionnaires.requestId = undefined;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getInfo.fulfilled, getInfo.rejected),
            (state: UserState, action) => {
                if (
                    state.info.loading === true &&
                    action.meta.requestId === state.info.requestId
                ) {
                    state.info.loading = false;
                    state.info.requestId = undefined;
                }
            },
        );
    },
});

const state = (state: AppState) => state.common.user;

const UserSlice = {
    actions: { getActiveQuestionnaires, getInfo },
    reducer: slice.reducer,
    state: {
        activeQuestionnaires: createSelector(
            state,
            (state: UserState) => state.activeQuestionnaires,
        ),
        activeQuestionnaire: createSelector(
            getActiveQuestionnaires,
            (questionnaires) => {
                if (questionnaires?.length > 0) {
                    return questionnaires[0];
                }

                return null;
            },
        ),
        info: createSelector(state, (state: UserState) => state.info),
    },
};

export default UserSlice;
